import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatDate, formatCurrency } from '@/lib/utils'
import { Plus, FileText, Receipt, Eye } from 'lucide-react'
import type { ProjectInvoice, ProjectQuote } from '@/types/project'

interface InvoicesQuotesTabProps {
  projectId: string
  clientId: string
  invoices?: ProjectInvoice[]
  quotes?: ProjectQuote[]
}

function InvoiceStatusBadge({ status }: { status: ProjectInvoice['statut'] }) {
  const config: Record<ProjectInvoice['statut'], { label: string; color: 'gray' | 'blue' | 'gold' | 'mint' | 'red' }> = {
    brouillon: { label: 'Brouillon', color: 'gray' },
    envoyee: { label: 'Envoyee', color: 'blue' },
    en_attente: { label: 'En attente', color: 'gold' },
    payee: { label: 'Payee', color: 'mint' },
    annulee: { label: 'Annulee', color: 'red' },
  }
  return <Badge variant={config[status].color}>{config[status].label}</Badge>
}

function QuoteStatusBadge({ status }: { status: ProjectQuote['status'] }) {
  const config: Record<ProjectQuote['status'], { label: string; color: 'gray' | 'blue' | 'mint' | 'red' | 'gold' }> = {
    brouillon: { label: 'Brouillon', color: 'gray' },
    envoye: { label: 'Envoye', color: 'blue' },
    accepte: { label: 'Accepte', color: 'mint' },
    refuse: { label: 'Refuse', color: 'red' },
    expire: { label: 'Expire', color: 'gold' },
  }
  return <Badge variant={config[status].color}>{config[status].label}</Badge>
}

export function InvoicesQuotesTab({
  projectId,
  clientId,
  invoices = [],
  quotes = [],
}: InvoicesQuotesTabProps) {
  const paidInvoices = invoices.filter((inv) => inv.statut === 'payee')
  const acceptedQuotes = quotes.filter((q) => q.status === 'accepte')

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-brown-500">Devis</p>
              <p className="text-lg font-semibold text-brown-900">{quotes.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-mint-600" />
            </div>
            <div>
              <p className="text-xs text-brown-500">Devis acceptes</p>
              <p className="text-lg font-semibold text-brown-900">
                {formatCurrency(acceptedQuotes.reduce((sum, q) => sum + q.total_ht, 0))}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-gold-600" />
            </div>
            <div>
              <p className="text-xs text-brown-500">Factures</p>
              <p className="text-lg font-semibold text-brown-900">{invoices.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-mint-600" />
            </div>
            <div>
              <p className="text-xs text-brown-500">Factures payees</p>
              <p className="text-lg font-semibold text-brown-900">
                {formatCurrency(paidInvoices.reduce((sum, inv) => sum + inv.total_ht, 0))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quotes section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-brown-900">Devis</h3>
          <Link to={`/devis/new?client=${clientId}&project=${projectId}`}>
            <Button variant="outline" size="sm" leftIcon={<Plus className="w-4 h-4" />}>
              Nouveau devis
            </Button>
          </Link>
        </div>

        {quotes.length === 0 ? (
          <Card padding="md">
            <EmptyState
              icon={<FileText className="w-10 h-10" />}
              title="Aucun devis"
              description="Aucun devis n'a ete cree pour ce projet"
              action={
                <Link to={`/devis/new?client=${clientId}&project=${projectId}`}>
                  <Button size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                    Creer un devis
                  </Button>
                </Link>
              }
            />
          </Card>
        ) : (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-beige-50 border-b border-beige-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Numero
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Statut
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Date
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Validite
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Montant HT
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-200">
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-beige-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-brown-900">{quote.numero}</span>
                      </td>
                      <td className="px-4 py-3">
                        <QuoteStatusBadge status={quote.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-brown-600">
                        {formatDate(quote.date_emission, { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-brown-600">
                        {formatDate(quote.date_validite, { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-brown-900">
                        {formatCurrency(quote.total_ht)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/devis/${quote.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Invoices section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-brown-900">Factures</h3>
          <Link to={`/invoices/new?client=${clientId}&project=${projectId}`}>
            <Button variant="outline" size="sm" leftIcon={<Plus className="w-4 h-4" />}>
              Nouvelle facture
            </Button>
          </Link>
        </div>

        {invoices.length === 0 ? (
          <Card padding="md">
            <EmptyState
              icon={<Receipt className="w-10 h-10" />}
              title="Aucune facture"
              description="Aucune facture n'a ete creee pour ce projet"
              action={
                <Link to={`/invoices/new?client=${clientId}&project=${projectId}`}>
                  <Button size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                    Creer une facture
                  </Button>
                </Link>
              }
            />
          </Card>
        ) : (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-beige-50 border-b border-beige-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Numero
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Statut
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Emission
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Echeance
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Montant HT
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Montant TTC
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-beige-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-brown-900">{invoice.numero}</span>
                      </td>
                      <td className="px-4 py-3">
                        <InvoiceStatusBadge status={invoice.statut} />
                      </td>
                      <td className="px-4 py-3 text-sm text-brown-600">
                        {formatDate(invoice.date_emission, { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-brown-600">
                        {formatDate(invoice.date_echeance, { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-brown-900">
                        {formatCurrency(invoice.total_ht)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-brown-900">
                        {formatCurrency(invoice.total_ttc)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/invoices/${invoice.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default InvoicesQuotesTab
